import React, { useState, useEffect, useRef } from "react";
import { useAtom } from "jotai/index";
import { isListeningAtom, isBubbleVisibleAtom, standbyModeAtom } from "~/Atom";
import { talkModes } from "~/Commons";
import "~/css/ThoughtBubble.css";
import * as icon from "~/Icons";

// パステルカラー定義
const pastelColors = [
  "#ff8ec6", // パステルピンク
  "#ff8eff", // パステルパープル
  "#c68eff", // パステルバイオレット
  "#8e8eff", // パステルブルー
  "#8ec6ff", // パステルライトブルー
  "#8effff", // パステルシアン
  "#8effc6", // パステルグリーン
  "#8eff8e", // パステルライトグリーン
  "#c6ff8e", // パステルイエローグリーン
  "#ffff8e", // パステルイエロー
  "#ffc68e", // パステルオレンジ
];

// 吹き出しのアイコン定義
const contentBubbleIcons = [
  { name: "WiTime12", component: <icon.WiTime12 className="bubble-icon" /> },
  { name: "WiEarthquake", component: <icon.WiEarthquake className="bubble-icon" /> },
  { name: "VscCodeReview", component: <icon.VscCodeReview className="bubble-icon" /> },
  { name: "TiHeartHalfOutline", component: <icon.TiHeartHalfOutline className="bubble-icon" /> },
  { name: "VscSparkle", component: <icon.VscSparkle className="bubble-icon" /> },
  { name: "TiWiFi", component: <icon.TiWiFi className="bubble-icon" /> },
  { name: "TbBellRingingFilled", component: <icon.TbBellRingingFilled className="bubble-icon" /> },
  { name: "TbBolt", component: <icon.TbBolt className="bubble-icon" /> },
  { name: "TbBubble", component: <icon.TbBubble className="bubble-icon" /> },
  { name: "TbFlask2Filled", component: <icon.TbFlask2Filled className="bubble-icon" /> },
  { name: "TbGiftFilled", component: <icon.TbGiftFilled className="bubble-icon" /> },
  { name: "TbMessageChatbot", component: <icon.TbMessageChatbot className="bubble-icon" /> },
  { name: "PiBookmarksFill", component: <icon.PiBookmarksFill className="bubble-icon" /> },
  {
    name: "IoMusicalNotesOutline",
    component: <icon.IoMusicalNotesOutline className="bubble-icon" />,
  },
  { name: "BiCoffeeTogo", component: <icon.BiCoffeeTogo className="bubble-icon" /> },
];

// 吹き出しの表示状態定義
const BubbleVisibleModes = {
  hidden: 0,
  fadeIn: 1,
  loop: 2,
  fadeOut: 3,
};

/**
 * 吹き出しアニメーション
 */
const ThoughtBubble: React.FC = () => {
  const [isListening] = useAtom(isListeningAtom);
  const [standbyMode] = useAtom(standbyModeAtom);
  const [currentBubbleIcon, setCurrentBubbleIcon] = useState<React.JSX.Element | undefined>(
    undefined
  ); // 表示アイコン
  const [isBubbleVisible] = useAtom(isBubbleVisibleAtom); // 表示状態を管理: true: 表示, false: 非表示
  const [isBubbleVisibleMode, setIsBubbleVisibleMode] = useState(0); // 吹き出しの表示状態を管理: BubbleVisibleModes
  const [currentColorIndex, setCurrentColorIndex] = useState(0); // 現在の色インデックス
  const isExecFadeInAnimation = useRef(false); // フェードインアニメーションが実行済みかどうかを管理
  const contentBubbleIconRef = useRef<HTMLSpanElement>(null); // アイコン要素への参照
  const [shuffledIcons, setShuffledIcons] = useState<typeof contentBubbleIcons>([]);

  /**
   * isBubbleVisibleがtrueになった際にフェードインアニメーションを開始
   * 吹き出し表示ごとにアイコンをシャッフル
   */
  useEffect(() => {
    if (isBubbleVisible) {
      // アイコン配列をシャッフル
      const newShuffledIcons = [...contentBubbleIcons].sort(() => Math.random() - 0.5);
      setShuffledIcons(newShuffledIcons);
      // フェードインアニメーションを開始
      setIsBubbleVisibleMode(BubbleVisibleModes.fadeIn);
      isExecFadeInAnimation.current = true;
    }
  }, [isBubbleVisible]);

  /**
   * isBubbleVisibleがfalseになった時のフェードアウト処理
   */
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!isBubbleVisible) {
      // 現在表示中の場合はフェードアウトしてから非表示にする
      if (isBubbleVisibleMode !== BubbleVisibleModes.hidden) {
        setIsBubbleVisibleMode(BubbleVisibleModes.fadeOut);
        timeoutId = setTimeout(() => {
          setIsBubbleVisibleMode(BubbleVisibleModes.hidden);
        }, 1000); // animation: scale-fade-out 1s
      }
    }

    // クリーンアップでタイマーをクリア
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isBubbleVisible, isBubbleVisibleMode]);

  /**
   * アニメーション繰り返しイベントの設定とアイコン切り替え処理
   */
  useEffect(() => {
    if (!isBubbleVisible || !contentBubbleIconRef.current || shuffledIcons.length === 0) return;

    const iconElement = contentBubbleIconRef.current;
    // アイコンの配列をシャッフル
    let iconIndex = 0;
    let colorIndex = 0;

    // 初回は先頭の要素を設定
    setCurrentBubbleIcon(shuffledIcons[iconIndex].component);
    setCurrentColorIndex(colorIndex);

    // アニメーション繰り返し時のイベントハンドラー
    const handleAnimationIteration = () => {
      // 次のアイコンに切り替え
      iconIndex = (iconIndex + 1) % shuffledIcons.length;
      setCurrentBubbleIcon(shuffledIcons[iconIndex].component);

      // 次の色に切り替え
      colorIndex = (colorIndex + 1) % pastelColors.length;
      setCurrentColorIndex(colorIndex);

      setIsBubbleVisibleMode(BubbleVisibleModes.loop);
    };

    // イベントリスナーを追加
    iconElement.addEventListener("animationiteration", handleAnimationIteration);

    // クリーンアップ
    // eslint-disable-next-line consistent-return
    return () => {
      iconElement.removeEventListener("animationiteration", handleAnimationIteration);
    };
  }, [isBubbleVisible, shuffledIcons]);

  /**
   * 吹き出しの表示状態に応じたクラス名を取得
   * @returns クラス名
   */
  const getVisibilityClass = () => {
    switch (isBubbleVisibleMode) {
      case BubbleVisibleModes.fadeIn:
        return "fade-in";
      case BubbleVisibleModes.loop:
        return "loop";
      case BubbleVisibleModes.fadeOut:
        return "fade-out";
      case BubbleVisibleModes.hidden:
        return "hidden";
      default:
        return "";
    }
  };

  // AI応答中でない場合はレンダリングしない
  if (isListening || (!isListening && standbyMode !== talkModes.active)) {
    // 現在表示中の場合はフェードアウトしてからDOMをレンダリングしないように調整
    if (isBubbleVisibleMode === BubbleVisibleModes.hidden) {
      isExecFadeInAnimation.current = false;
      return null;
    }
  } else if (!isBubbleVisible && !isExecFadeInAnimation.current) {
    // 非表示の場合は何もレンダリングしない
    return null;
  }

  return (
    <div className={`thought-bubble-container ${getVisibilityClass()}`}>
      {/* 吹き出し */}
      <span className="thought-bubble small"></span>
      <span className="thought-bubble medium"></span>
      <span className="thought-bubble large">
        {/* アイコン */}
        <span
          ref={contentBubbleIconRef}
          className="content-bubble-icon"
          style={{ color: pastelColors[currentColorIndex] }}
        >
          {currentBubbleIcon}
        </span>
      </span>
    </div>
  );
};

export default ThoughtBubble;
